import React from 'react';

/* ═══════════════════════════════════════════════════════════════════
   YOGA SILHOUETTE — procedural gold-line pose per yoga id.
   Same aesthetic as Power's animal silhouettes:
   thin gold strokes on obsidian, with a subtle halo glow.
   Every yoga gets its OWN distinct pose (no mismatch).
   0-cost, 0-storage — pure SVG.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD_BRIGHT = '#E9C77A';
const GOLD = '#C5A059';

/* Pose path library. Each path uses a 0..200 coordinate space.
   Human proportion: head r≈9, torso 24→100, arms & legs vary. */
const POSES = {
  /* Hatha · seated meditation — lotus, hands on knees, spine tall */
  hatha: {
    label: 'Lotus · Padmasana',
    paths: [
      /* head */
      'M 100 42 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0',
      /* spine */
      'M 100 52 L 100 120',
      /* arms — resting on knees */
      'M 100 78 C 78 84, 62 100, 60 130',
      'M 100 78 C 122 84, 138 100, 140 130',
      /* crossed legs — lotus shape */
      'M 100 120 C 78 130, 55 140, 52 156 C 60 158, 82 154, 100 148',
      'M 100 120 C 122 130, 145 140, 148 156 C 140 158, 118 154, 100 148',
      /* hands (mudra dots) */
      'M 58 128 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0',
      'M 136 128 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0',
    ],
  },

  /* Restorative · child's pose — folded forward, arms extended */
  restorative: {
    label: "Child's Pose · Balasana",
    paths: [
      /* head down on the mat */
      'M 90 148 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0',
      /* curved spine folded */
      'M 90 148 C 100 130, 120 116, 145 118',
      /* torso extending */
      'M 145 118 C 160 118, 170 122, 175 128',
      /* arms extended forward */
      'M 92 150 C 68 154, 44 158, 26 162',
      'M 92 150 C 68 158, 44 164, 26 170',
      /* folded legs behind (heels toward hips) */
      'M 145 118 C 160 130, 170 148, 174 168',
      'M 160 118 C 168 132, 174 152, 178 172',
    ],
  },

  /* Vinyasa · downward dog — inverted V */
  vinyasa: {
    label: 'Downward Dog · Adho Mukha',
    paths: [
      /* head between arms */
      'M 60 130 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0',
      /* torso from hips (peak) down to shoulders */
      'M 100 60 C 92 90, 78 118, 60 130',
      /* arms extended down to floor */
      'M 60 130 L 40 172',
      /* legs from peak to floor */
      'M 100 60 C 118 90, 140 130, 160 172',
      'M 100 60 C 122 90, 148 130, 168 172',
      /* hands and feet marks */
      'M 34 172 L 46 172',
      'M 154 172 L 174 172',
    ],
  },

  /* Power · chair (fierce) pose — knees bent, arms raised */
  power: {
    label: 'Chair · Utkatasana',
    paths: [
      /* head */
      'M 100 30 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0',
      /* spine tilted slightly forward */
      'M 100 40 L 96 100',
      /* arms overhead, palms together */
      'M 96 60 C 92 40, 88 22, 90 8',
      'M 96 60 C 100 40, 104 22, 102 8',
      /* thighs — bent as if sitting on invisible chair */
      'M 96 100 C 82 115, 72 135, 70 148',
      'M 96 100 C 110 115, 120 135, 122 148',
      /* shins vertical to floor */
      'M 70 148 L 68 180',
      'M 122 148 L 124 180',
      /* feet */
      'M 60 182 L 78 182',
      'M 116 182 L 134 182',
    ],
  },

  /* Iyengar · triangle pose */
  iyengar: {
    label: 'Triangle · Trikonasana',
    paths: [
      /* head tilted */
      'M 68 62 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0',
      /* torso tilted sideways from hip */
      'M 68 62 C 88 88, 108 108, 118 118',
      /* top arm reaching up */
      'M 100 80 L 132 30',
      /* bottom arm reaching toward front leg */
      'M 100 80 L 64 138',
      /* front leg (bent slightly) */
      'M 118 118 L 46 180',
      /* back leg (straight, extended right) */
      'M 118 118 L 178 180',
      /* feet */
      'M 38 182 L 58 182',
      'M 168 182 L 188 182',
    ],
  },

  /* Yin · pigeon pose */
  yin: {
    label: 'Pigeon · Kapotasana',
    paths: [
      /* head forward */
      'M 72 66 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0',
      /* torso arching upright */
      'M 78 74 C 88 88, 96 108, 100 130',
      /* arms — one down for support, one arching back */
      'M 82 90 C 62 108, 50 132, 46 158',
      'M 100 88 C 120 82, 138 74, 148 62',
      /* front leg (folded) */
      'M 100 130 C 82 138, 62 144, 40 148',
      /* back leg extended straight */
      'M 108 138 L 178 168',
      /* mat feet */
      'M 30 150 L 50 150',
      'M 168 170 L 188 170',
    ],
  },

  /* Kundalini · cobra pose */
  kundalini: {
    label: 'Cobra · Bhujangasana',
    paths: [
      /* head raised */
      'M 60 76 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0',
      /* arched spine — upward curl */
      'M 60 84 C 70 108, 90 128, 120 138',
      /* arms pressing into mat */
      'M 62 96 L 50 140',
      'M 74 96 L 82 140',
      /* legs extended flat, together */
      'M 120 138 C 148 140, 170 142, 188 144',
      'M 120 144 C 148 146, 170 148, 188 150',
      /* feet points */
      'M 184 140 L 192 148',
      'M 184 152 L 192 144',
    ],
  },

  /* Jivamukti · dancer pose (natarajasana) */
  jivamukti: {
    label: 'Dancer · Natarajasana',
    paths: [
      /* head */
      'M 76 36 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0',
      /* torso tilted forward */
      'M 76 44 C 84 66, 92 88, 96 110',
      /* front arm extended forward */
      'M 80 60 C 62 58, 44 58, 26 62',
      /* back arm reaching up-behind holding foot */
      'M 90 60 C 110 50, 128 40, 138 26',
      /* standing leg */
      'M 96 110 L 92 180',
      /* raised leg bent back — foot toward hand */
      'M 96 110 C 118 108, 138 90, 148 60',
      /* foot */
      'M 138 26 L 152 40',
      'M 82 182 L 102 182',
    ],
  },

  /* Ashtanga · warrior II (reference image) */
  ashtanga: {
    label: 'Warrior II · Virabhadrasana',
    paths: [
      /* head */
      'M 100 42 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0',
      /* upright torso */
      'M 100 52 L 100 110',
      /* arms outstretched horizontally */
      'M 100 78 L 30 78',
      'M 100 78 L 170 78',
      /* front leg bent 90° */
      'M 100 110 C 122 128, 138 148, 148 180',
      /* back leg extended straight */
      'M 100 110 C 76 128, 52 152, 28 180',
      /* palms */
      'M 24 74 L 24 82',
      'M 176 74 L 176 82',
      /* feet */
      'M 138 182 L 158 182',
      'M 18 182 L 38 182',
    ],
  },

  /* Hot · plank pose — straight-line body over hands */
  hot: {
    label: 'Plank · Kumbhakasana',
    paths: [
      /* head extended */
      'M 44 100 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0',
      /* long torso line */
      'M 50 106 L 160 118',
      /* arms straight down */
      'M 54 108 L 48 158',
      'M 62 106 L 60 158',
      /* legs extended */
      'M 160 118 L 190 130',
      /* feet on toes */
      'M 186 128 L 196 134',
      /* hands */
      'M 42 158 L 56 158',
      'M 54 158 L 68 158',
    ],
  },

  /* Bikram · standing bow */
  bikram: {
    label: 'Standing Bow · Dandayamana',
    paths: [
      /* head */
      'M 68 40 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0',
      /* torso tilted forward */
      'M 68 48 C 82 76, 94 100, 102 116',
      /* front arm extended forward */
      'M 74 62 C 54 58, 34 56, 14 60',
      /* back arm reaching up holding raised foot */
      'M 86 60 C 108 50, 128 42, 138 26',
      /* standing leg */
      'M 102 116 L 100 182',
      /* raised leg curled back */
      'M 102 116 C 128 106, 148 82, 142 30',
      /* foot */
      'M 138 26 L 148 40',
      'M 90 182 L 110 182',
    ],
  },

  /* Prenatal · seated wide-legged, arms cradle */
  prenatal: {
    label: 'Wide Seated · Baddha Konasana',
    paths: [
      /* head */
      'M 100 44 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0',
      /* spine tall */
      'M 100 54 L 100 116',
      /* arms embracing / hands on soles */
      'M 100 82 C 80 96, 68 112, 62 130',
      'M 100 82 C 120 96, 132 112, 138 130',
      /* legs bent, soles together (butterfly) */
      'M 100 116 C 82 122, 64 128, 50 140',
      'M 100 116 C 118 122, 136 128, 150 140',
      /* soles meeting */
      'M 50 140 L 100 152',
      'M 150 140 L 100 152',
      /* feet dots */
      'M 46 138 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0',
      'M 148 138 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0',
    ],
  },
};

/* Fallback — a simple standing figure for unknown ids (e.g. user-forged) */
const FALLBACK = {
  label: 'Practice',
  paths: [
    'M 100 40 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0',
    'M 100 50 L 100 130',
    'M 100 80 L 70 110',
    'M 100 80 L 130 110',
    'M 100 130 L 84 180',
    'M 100 130 L 116 180',
  ],
};

/**
 * YogaSilhouette
 * @param {string} yogaId — matches YOGA_STYLES id
 * @param {number} size — px
 * @param {string} stroke — gold hex
 * @param {boolean} glow — enables outer drop-shadow halo
 */
export const YogaSilhouette = ({ yogaId, size = 120, stroke = GOLD_BRIGHT, glow = true }) => {
  const pose = POSES[yogaId] || FALLBACK;
  const filterId = `yoga-glow-${yogaId || 'fb'}`;
  return (
    <svg width={size} height={size} viewBox="0 0 200 200"
      style={{ display: 'block', filter: glow ? `drop-shadow(0 0 8px ${stroke}77)` : 'none' }}
      role="img" aria-label={pose.label}>
      <defs>
        <radialGradient id={filterId} cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.16"/>
          <stop offset="70%" stopColor={stroke} stopOpacity="0.04"/>
          <stop offset="100%" stopColor={stroke} stopOpacity="0"/>
        </radialGradient>
      </defs>
      {/* soft halo behind */}
      <rect x="0" y="0" width="200" height="200" fill={`url(#${filterId})`}/>
      {pose.paths.map((d, i) => (
        <path key={i} d={d}
          fill="none"
          stroke={stroke}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.92}
        />
      ))}
      {/* second stroke pass for glow */}
      {pose.paths.map((d, i) => (
        <path key={`g${i}`} d={d}
          fill="none"
          stroke={stroke}
          strokeWidth="0.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.5}
          style={{ filter: `drop-shadow(0 0 3px ${stroke})` }}
        />
      ))}
    </svg>
  );
};

export const YOGA_POSE_LABELS = Object.fromEntries(
  Object.entries(POSES).map(([k, v]) => [k, v.label])
);

export { GOLD, GOLD_BRIGHT };
export default YogaSilhouette;
